import { api } from "./api";
import { AxiosError } from "axios";
import type {
  ShiftCreateInput,
  ShiftResponse,
  ShiftUpdateInput,
  ShiftsCreatedResponse,
  ShiftsResponse,
} from "@/types/shift.type";

// Récupère les shifts sur une période (from / to obligatoires côté backend)
export const getShifts = async (params: {
  from: string;
  to: string;
  employeeId?: string;
  companyId?: string;
}) => {
  try {
    const response = await api.get<ShiftsResponse>("/shifts", { params });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la récupération des shifts"
      );
    }
    throw new Error("Erreur lors de la récupération des shifts");
  }
};

// Crée un ou plusieurs shifts (batch)
// - un objet simple : ShiftCreateInput
// - ou un batch : { shifts: ShiftCreateInput[] }
// Le backend peut aussi renvoyer un 409 quand TOUS les shifts sont en conflit ;
// dans ce cas on retourne quand même la réponse pour que l'UI puisse afficher le détail.
export const createShifts = async (
  payload: ShiftCreateInput | { shifts: ShiftCreateInput[] }
) => {
  try {
    const response = await api.post<ShiftsCreatedResponse>("/shifts", payload);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const data = error.response?.data as ShiftsCreatedResponse | undefined;
      // 409 : aucun shift créé (tous en conflit). On remonte la data typée.
      if (error.response?.status === 409 && data?.skipped) {
        return data;
      }
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la création des shifts"
      );
    }
    throw new Error("Erreur lors de la création des shifts");
  }
};

// Modifie un shift existant (patch partiel)
export const updateShift = async (id: string, data: ShiftUpdateInput) => {
  try {
    const response = await api.patch<ShiftResponse>(`/shifts/${id}`, data);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la modification du shift"
      );
    }
    throw new Error("Erreur lors de la modification du shift");
  }
};

// Publie tous les shifts non publiés d'une semaine donnée
export const publishShifts = async (params: {
  from: string;
  to: string;
  companyId?: string;
}) => {
  try {
    const response = await api.post<{ message: string; published: number }>(
      "/shifts/publish",
      params
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message || "Erreur lors de la publication"
      );
    }
    throw new Error("Erreur lors de la publication");
  }
};

// Supprime un shift
export const deleteShift = async (id: string) => {
  try {
    const response = await api.delete<{ message: string }>(`/shifts/${id}`);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la suppression du shift"
      );
    }
    throw new Error("Erreur lors de la suppression du shift");
  }
};
